import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Plus, Trash2, Save, Loader2, Star, ChevronLeft,
  MessageSquare, List as ListIcon, MousePointerClick, Paperclip,
  HelpCircle, GitBranch, Zap, UserRound,
} from 'lucide-react';
import ChatbotPreview from './ChatbotPreview';

/**
 * ChatbotTreeEditor — pantalla donde un admin arma el árbol del chatbot visual (F3).
 * Reúsa el render (ChatbotPreview → InteractiveMessage) y el backend de flows:
 *   GET/PUT  /admin/anchor/chatbot-tree/{company_id}
 *   POST     /admin/anchor/chatbot-tree/{company_id}/preview {node} → {messages:[v3]}
 * El MOTOR arma el mensaje; el editor solo edita el árbol y dibuja la vista previa.
 * Se monta embebido desde Número Ancla (prop companyId) o por ruta (?company=...).
 * Contrato de nodos/límites: ver docs/CONTRATO-editor-chatbot.
 */

const TIPOS = [
  { v: 'mensaje', label: 'Mensaje' },
  { v: 'lista', label: 'Lista de opciones' },
  { v: 'botones', label: 'Botones' },
  { v: 'material', label: 'Material (archivos)' },
  { v: 'pedir_dato', label: 'Pedir un dato' },
  { v: 'rama_intencion', label: 'Rama por intención' },
  { v: 'conversion', label: 'Acción (cita / llamada)' },
  { v: 'handoff', label: 'Pasar a un humano' },
];
const TIPO_META = {
  mensaje: { label: 'Mensaje', Icon: MessageSquare },
  lista: { label: 'Lista', Icon: ListIcon },
  botones: { label: 'Botones', Icon: MousePointerClick },
  material: { label: 'Material', Icon: Paperclip },
  pedir_dato: { label: 'Pedir dato', Icon: HelpCircle },
  rama_intencion: { label: 'Rama', Icon: GitBranch },
  conversion: { label: 'Acción', Icon: Zap },
  handoff: { label: 'A humano', Icon: UserRound },
};
const CAPTURA = [{ v: 'nombre', label: 'Nombre' }, { v: 'email', label: 'Correo' }, { v: 'telefono', label: 'Teléfono' }];
const ACCIONES = [
  { v: 'AGENDAR_CITA', label: 'Agendar cita' },
  { v: 'PROPONER_CITA', label: 'Proponer cita' },
  { v: 'LLAMAR_IA', label: 'Llamada con IA' },
  { v: 'LLAMAR_AHORA', label: 'Llamar ahora' },
];
const ADJ_TIPOS = [{ v: 'image', label: 'Imagen' }, { v: 'document', label: 'Documento' }, { v: 'video', label: 'Video' }, { v: 'audio', label: 'Audio' }];
// Nota bajo la vista previa para pasos que disparan una acción (no solo un mensaje).
const ACCION_HINT = {
  conversion: (n) => `Además del texto, dispara: ${(ACCIONES.find((a) => a.v === n.accion?.tag) || {}).label || 'una acción'}.`,
  handoff: () => 'Pasa la conversación a un humano y termina el flujo.',
  rama_intencion: () => 'Enruta según la intención que exprese el lead (sin mensaje visible).',
};
// Límites que valida el motor (guardrales anti-pendejos: avisamos ANTES de que trunque).
const LIM = { titulo_boton: 20, titulo_lista: 24, desc: 72, copy: 1024, encabezado: 60, pie: 60, max_botones: 3, max_filas: 10 };

const EMPTY = { root: '', nodes: {} };
const uid = () => 'n_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
const arr = (x) => (Array.isArray(x) ? x : []);
const etiquetaNodo = (n) => {
  const m = TIPO_META[n?.tipo];
  const c = (n?.copy || '').slice(0, 24);
  return `${m?.label || n?.tipo || '?'}${c ? ': ' + c : ''}`;
};

// ── Helpers de UI ────────────────────────────────────────────────────
function Sel({ value, onChange, children, disabled }) {
  return (
    <select
      value={value} disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
    >
      {children}
    </select>
  );
}
function Cont({ len, max }) {
  return <span className={`text-[10px] tabular-nums ${len > max ? 'text-amber-500' : 'text-muted-foreground'}`}>{len}/{max}</span>;
}
function Campo({ label, len, max, children }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs">{label}</Label>
        {typeof len === 'number' && typeof max === 'number' && <Cont len={len} max={max} />}
      </div>
      {children}
    </div>
  );
}

// Editor de salidas (opciones de lista/botones, o ramas por intención)
function SalidasEditor({ salidas, nodes, currentId, onChange, allowDescripcion, max, labelMax, field = 'label', labelName = 'Opción' }) {
  const rows = arr(salidas);
  const opts = Object.values(nodes).filter((n) => n.id !== currentId);
  const set = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const add = () => onChange([...rows, field === 'intent' ? { intent: '', destino: '' } : { label: '', destino: '', ...(allowDescripcion ? { descripcion: '' } : {}) }]);
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));
  const atMax = !!max && rows.length >= max;
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="rounded-md border border-border p-2 space-y-2">
          <div className="flex items-center gap-2">
            <Input
              value={r[field] || ''}
              onChange={(e) => set(i, { [field]: e.target.value })}
              placeholder={field === 'intent' ? 'intención (ej. precio)' : `${labelName} ${i + 1}`}
              className="h-8"
            />
            {!!labelMax && <Cont len={(r[field] || '').length} max={labelMax} />}
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => remove(i)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground shrink-0">va a →</span>
            <Sel value={r.destino || ''} onChange={(v) => set(i, { destino: v })}>
              <option value="">(sin destino)</option>
              {opts.map((n) => (<option key={n.id} value={n.id}>{etiquetaNodo(n)}</option>))}
            </Sel>
          </div>
          {allowDescripcion && (
            <div className="flex items-center gap-2">
              <Input value={r.descripcion || ''} onChange={(e) => set(i, { descripcion: e.target.value })} placeholder="descripción (opcional)" className="h-8" />
              <Cont len={(r.descripcion || '').length} max={LIM.desc} />
            </div>
          )}
        </div>
      ))}
      {!atMax && (
        <Button variant="outline" size="sm" onClick={add}>
          <Plus className="h-4 w-4 mr-1" /> Agregar {labelName.toLowerCase()}
        </Button>
      )}
      {atMax && <p className="text-[11px] text-amber-500">Máximo {max} botones. Para más opciones, usa una Lista.</p>}
    </div>
  );
}

// Editor de adjuntos (material)
function AdjuntosEditor({ adjuntos, onChange }) {
  const rows = arr(adjuntos);
  const set = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const add = () => onChange([...rows, { tipo: 'image', url: '', filename: '', caption: '' }]);
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="rounded-md border border-border p-2 space-y-2">
          <div className="flex items-center gap-2">
            <Sel value={r.tipo || 'image'} onChange={(v) => set(i, { tipo: v })}>
              {ADJ_TIPOS.map((a) => (<option key={a.v} value={a.v}>{a.label}</option>))}
            </Sel>
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => remove(i)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <Input value={r.url || ''} onChange={(e) => set(i, { url: e.target.value })} placeholder="URL del archivo (https://…)" className="h-8" />
          {r.tipo === 'document' && (
            <Input value={r.filename || ''} onChange={(e) => set(i, { filename: e.target.value })} placeholder="nombre del archivo (opcional)" className="h-8" />
          )}
          {r.tipo !== 'audio' && (
            <Input value={r.caption || ''} onChange={(e) => set(i, { caption: e.target.value })} placeholder="texto/pie (opcional)" className="h-8" />
          )}
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add}>
        <Plus className="h-4 w-4 mr-1" /> Agregar archivo
      </Button>
    </div>
  );
}

// ── Editor principal ─────────────────────────────────────────────────
export default function ChatbotTreeEditor({ companyId: propCompanyId, companyName, onBack }) {
  const [sp] = useSearchParams();
  const companyId = propCompanyId || sp.get('company') || '';
  const [tree, setTree] = useState(EMPTY);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    let cancel = false;
    setLoading(true);
    api.get(`/admin/anchor/chatbot-tree/${companyId}`)
      .then((r) => {
        if (cancel) return;
        const t = r.data?.chat_tree && typeof r.data.chat_tree === 'object' ? r.data.chat_tree : EMPTY;
        const norm = { root: t.root || '', nodes: t.nodes || {} };
        setTree(norm);
        setSelectedId(norm.root || Object.keys(norm.nodes)[0] || '');
      })
      .catch(() => { if (!cancel) { setTree(EMPTY); toast.error('No se pudo cargar el chatbot (¿el endpoint aún no está desplegado?).'); } })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [companyId]);

  const nodes = tree.nodes || {};
  const nodeList = useMemo(() => Object.values(nodes), [nodes]);
  const selected = nodes[selectedId] || null;

  const mutate = (fn) => { setTree((prev) => fn(JSON.parse(JSON.stringify(prev)))); setDirty(true); };
  const patchNode = (id, patch) => mutate((t) => { t.nodes[id] = { ...t.nodes[id], ...patch }; return t; });

  const addNode = () => {
    const id = uid();
    mutate((t) => { t.nodes[id] = { id, tipo: 'mensaje', copy: '' }; if (!t.root) t.root = id; return t; });
    setSelectedId(id);
  };
  const deleteNode = (id) => {
    if (!window.confirm('¿Borrar este paso? Las opciones que apunten a él quedarán sin destino.')) return;
    mutate((t) => {
      delete t.nodes[id];
      if (t.root === id) t.root = Object.keys(t.nodes)[0] || '';
      Object.values(t.nodes).forEach((n) => {
        if (Array.isArray(n.salidas)) n.salidas.forEach((s) => { if (s.destino === id) s.destino = ''; });
        if (n.continuar === id) n.continuar = '';
      });
      return t;
    });
    if (selectedId === id) setSelectedId(Object.keys(nodes).filter((k) => k !== id)[0] || '');
  };
  const setRoot = (id) => mutate((t) => { t.root = id; return t; });

  const save = async () => {
    if (!companyId) return;
    if (!tree.root || !tree.nodes[tree.root]) { toast.error('Marca un paso inicial con la estrella antes de guardar.'); return; }
    setSaving(true);
    try {
      await api.put(`/admin/anchor/chatbot-tree/${companyId}`, { chat_tree: tree });
      setDirty(false);
      toast.success('Chatbot guardado.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar.');
    } finally { setSaving(false); }
  };

  const renderTipoFields = (n) => {
    const cambia = (patch) => patchNode(n.id, patch);
    const t = n.tipo;
    const showCab = t === 'lista' || t === 'botones';
    const showCopy = t !== 'rama_intencion';
    const copyLabel = t === 'pedir_dato' ? 'Pregunta / instrucción' : t === 'material' ? 'Texto (opcional)' : 'Texto';
    const conContinuar = t === 'mensaje' || t === 'material' || t === 'conversion' || t === 'pedir_dato';
    return (
      <div className="space-y-3">
        {showCab && (
          <Campo label="Encabezado (opcional)" len={(n.encabezado || '').length} max={LIM.encabezado}>
            <Input value={n.encabezado || ''} onChange={(e) => cambia({ encabezado: e.target.value })} placeholder="título del mensaje" />
          </Campo>
        )}
        {showCopy && (
          <Campo label={copyLabel} len={(n.copy || '').length} max={LIM.copy}>
            <Textarea
              value={n.copy || ''} onChange={(e) => cambia({ copy: e.target.value })} rows={3}
              placeholder={t === 'pedir_dato' ? 'Ej. ¿Me compartes tu nombre?' : 'Escribe el mensaje…'}
            />
          </Campo>
        )}
        {showCab && (
          <Campo label="Pie (opcional)" len={(n.pie || '').length} max={LIM.pie}>
            <Input value={n.pie || ''} onChange={(e) => cambia({ pie: e.target.value })} placeholder="texto pequeño al final" />
          </Campo>
        )}

        {t === 'pedir_dato' && (
          <div className="space-y-1">
            <Label className="text-xs">¿Qué dato pide?</Label>
            <Sel value={n.captura_campo || 'nombre'} onChange={(v) => cambia({ captura_campo: v })}>
              {CAPTURA.map((c) => (<option key={c.v} value={c.v}>{c.label}</option>))}
            </Sel>
          </div>
        )}

        {t === 'lista' && (
          <>
            <Campo label="Título de la sección (opcional)" len={(n.seccion || '').length} max={LIM.titulo_lista}>
              <Input value={n.seccion || ''} onChange={(e) => cambia({ seccion: e.target.value })} placeholder="ej. Opciones" />
            </Campo>
            <div className="space-y-1">
              <Label className="text-xs">
                Opciones{' '}
                {arr(n.salidas).length > LIM.max_filas && (
                  <span className="text-amber-500">(más de {LIM.max_filas}: se paginan con “Ver más”)</span>
                )}
              </Label>
              <SalidasEditor salidas={n.salidas} nodes={nodes} currentId={n.id} onChange={(s) => cambia({ salidas: s })} allowDescripcion labelMax={LIM.titulo_lista} labelName="Opción" />
            </div>
          </>
        )}

        {t === 'botones' && (
          <div className="space-y-1">
            <Label className="text-xs">Botones (máx {LIM.max_botones})</Label>
            <SalidasEditor salidas={n.salidas} nodes={nodes} currentId={n.id} onChange={(s) => cambia({ salidas: s })} allowDescripcion={false} max={LIM.max_botones} labelMax={LIM.titulo_boton} labelName="Botón" />
          </div>
        )}

        {t === 'material' && (
          <div className="space-y-1">
            <Label className="text-xs">Archivos</Label>
            <AdjuntosEditor adjuntos={n.adjuntos} onChange={(a) => cambia({ adjuntos: a })} />
          </div>
        )}

        {t === 'rama_intencion' && (
          <div className="space-y-1">
            <Label className="text-xs">Ramas por intención</Label>
            <p className="text-[11px] text-muted-foreground">Enruta según lo que el lead escribe. Este paso no envía un mensaje visible.</p>
            <SalidasEditor salidas={n.salidas} nodes={nodes} currentId={n.id} onChange={(s) => cambia({ salidas: s })} allowDescripcion={false} field="intent" labelName="Intención" />
          </div>
        )}

        {t === 'conversion' && (
          <div className="space-y-1">
            <Label className="text-xs">Acción</Label>
            <Sel value={n.accion?.tag || 'AGENDAR_CITA'} onChange={(v) => cambia({ accion: { ...(n.accion || {}), tag: v } })}>
              {ACCIONES.map((a) => (<option key={a.v} value={a.v}>{a.label}</option>))}
            </Sel>
            <Input value={n.accion?.valor || ''} onChange={(e) => cambia({ accion: { ...(n.accion || {}), valor: e.target.value } })} placeholder="valor (opcional)" className="mt-1" />
          </div>
        )}

        {t === 'handoff' && (
          <p className="text-[11px] text-muted-foreground">Este paso pasa la conversación a un humano y termina el flujo.</p>
        )}

        {conContinuar && (
          <div className="space-y-1">
            <Label className="text-xs">Después de este paso, ir a (opcional)</Label>
            <Sel value={n.continuar || ''} onChange={(v) => cambia({ continuar: v })}>
              <option value="">(fin / esperar respuesta)</option>
              {Object.values(nodes).filter((x) => x.id !== n.id).map((x) => (<option key={x.id} value={x.id}>{etiquetaNodo(x)}</option>))}
            </Sel>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Encabezado */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ChevronLeft className="h-4 w-4 mr-1" /> Volver
            </Button>
          )}
          <div className="min-w-0">
            <h2 className="text-lg font-semibold truncate">Configurar chatbot</h2>
            <p className="text-xs text-muted-foreground truncate">{companyName || companyId || '—'}</p>
          </div>
        </div>
        <Button onClick={save} disabled={saving || !dirty}>
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Guardar
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
      ) : !companyId ? (
        <p className="text-sm text-amber-500">Falta indicar el tenant.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4">
          {/* Lista de pasos */}
          <Card>
            <CardHeader className="p-3"><CardTitle className="text-sm">Pasos</CardTitle></CardHeader>
            <CardContent className="p-3 pt-0 space-y-1">
              {nodeList.length === 0 && <p className="text-xs text-muted-foreground">Aún no hay pasos. Agrega el primero.</p>}
              {nodeList.map((n) => {
                const M = TIPO_META[n.tipo] || {};
                const Icon = M.Icon || MessageSquare;
                return (
                  <button
                    key={n.id} onClick={() => setSelectedId(n.id)}
                    className={`w-full text-left rounded-md px-2 py-1.5 flex items-center gap-2 text-sm border ${selectedId === n.id ? 'bg-primary/10 border-primary/30' : 'border-transparent hover:bg-muted'}`}
                  >
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="truncate flex-1">{(n.copy || '').slice(0, 20) || M.label || n.tipo}</span>
                    {tree.root === n.id && <Star className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                  </button>
                );
              })}
              <Button variant="outline" size="sm" className="w-full mt-2" onClick={addNode}>
                <Plus className="h-4 w-4 mr-1" /> Paso
              </Button>
            </CardContent>
          </Card>

          {/* Formulario + vista previa */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="p-3 flex-row items-center justify-between space-y-0">
                <CardTitle className="text-sm">Editar paso</CardTitle>
                {selected && (
                  <div className="flex items-center gap-1">
                    {tree.root !== selected.id && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Marcar como paso inicial" onClick={() => setRoot(selected.id)}>
                        <Star className="h-4 w-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Borrar paso" onClick={() => deleteNode(selected.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="p-3 pt-0 space-y-3">
                {!selected ? (
                  <p className="text-xs text-muted-foreground">Selecciona un paso de la izquierda o agrega uno.</p>
                ) : (
                  <>
                    <div className="space-y-1">
                      <Label className="text-xs">Tipo de paso</Label>
                      <Sel value={selected.tipo} onChange={(v) => patchNode(selected.id, { tipo: v })}>
                        {TIPOS.map((t) => (<option key={t.v} value={t.v}>{t.label}</option>))}
                      </Sel>
                    </div>
                    {renderTipoFields(selected)}
                  </>
                )}
              </CardContent>
            </Card>

            <div className="space-y-2">
              {selected && ACCION_HINT[selected.tipo] && (() => {
                const Icon = TIPO_META[selected.tipo]?.Icon || Zap;
                return (
                  <div className="rounded-lg border border-primary/30 bg-primary/5 p-2 text-xs flex items-center gap-2">
                    <Icon className="h-3.5 w-3.5 text-primary shrink-0" />
                    <span>{ACCION_HINT[selected.tipo](selected)}</span>
                  </div>
                );
              })()}
              <ChatbotPreview companyId={companyId} node={selected} />
              <p className="text-[11px] text-muted-foreground">
                La vista previa la genera el motor real. Guarda para que el chatbot use estos pasos.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
